import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaHeart, FaRegHeart } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
    useFavoritesMutation,
    useRemoveFavoritesMutation,
    useWishlistQuery,
} from '../slices/usersApiSlice';

// Heart for the corner of a product card. Every heart on a page shares one
// wishlist request, rather than asking the server once per product.
const WishlistHeart = ({ productId, productName }) => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { userInfo } = useSelector((state) => state.auth);

    const { data: wishlist } = useWishlistQuery(userInfo?._id, { skip: !userInfo });
    const [addToWishlist, { isLoading: isAdding }] = useFavoritesMutation();
    const [removeFromWishlist, { isLoading: isRemoving }] = useRemoveFavoritesMutation();

    // Shows the new state straight away, until the refreshed wishlist arrives
    const [pendingSaved, setPendingSaved] = useState(null);
    useEffect(() => setPendingSaved(null), [wishlist]);

    const isSaved = pendingSaved ?? !!wishlist?.some((item) => item?._id === productId);

    const handleClick = async () => {
        if (!userInfo) {
            navigate(`/login?redirect=${pathname}`);
            return;
        }
        if (isAdding || isRemoving) return;

        setPendingSaved(!isSaved);
        try {
            if (isSaved) {
                await removeFromWishlist({ productId }).unwrap();
            } else {
                await addToWishlist({ productId }).unwrap();
            }
        } catch (err) {
            setPendingSaved(null);
            toast.error(`Couldn't update your wishlist. Please try again.`);
        }
    };

    return (
        <button
            type="button"
            className={`wishlist-heart${isSaved ? ' is-saved' : ''}`}
            onClick={handleClick}
            aria-pressed={isSaved}
            aria-label={`${isSaved ? 'Remove' : 'Add'} ${productName} ${isSaved ? 'from' : 'to'} wishlist`}
        >
            {isSaved ? <FaHeart aria-hidden="true" /> : <FaRegHeart aria-hidden="true" />}
        </button>
    );
};

export default WishlistHeart;
